/**
 * Search Context Storage Utility
 * 
 * Manages saving and restoring search context across payment flows.
 * Uses sessionStorage to persist search state during Stripe checkout redirects.
 */

export interface SearchContext {
  address: string;
  lat: number;
  lng: number;
  zoneProperties?: any;
  parcelData?: any;
  timestamp: number;
}

const STORAGE_KEY = 'zoningpal_search_context';
const CONTEXT_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Save search context to sessionStorage
 */
export function saveSearchContext(
  address: string,
  lat: number,
  lng: number,
  zoneProperties?: any,
  parcelData?: any
): void {
  try {
    const context: SearchContext = {
      address,
      lat,
      lng,
      zoneProperties,
      parcelData,
      timestamp: Date.now()
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    console.log('✅ Search context saved:', { address, lat, lng });
  } catch (error) {
    console.error('❌ Failed to save search context:', error);
  }
}

/**
 * Get saved search context from sessionStorage
 * Returns null if not found or expired
 */
export function getSearchContext(): SearchContext | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const context: SearchContext = JSON.parse(stored);
    
    // Check if context has expired (older than 30 minutes)
    if (Date.now() - context.timestamp > CONTEXT_EXPIRY_MS) {
      console.log('⚠️ Search context expired, clearing');
      clearSearchContext();
      return null;
    }

    console.log('✅ Search context retrieved:', { 
      address: context.address, 
      lat: context.lat, 
      lng: context.lng,
      age: Math.round((Date.now() - context.timestamp) / 1000) + 's'
    });
    
    return context;
  } catch (error) {
    console.error('❌ Failed to retrieve search context:', error);
    return null;
  }
}

/**
 * Clear saved search context
 */
export function clearSearchContext(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    console.log('🗑️ Search context cleared');
  } catch (error) {
    console.error('❌ Failed to clear search context:', error);
  }
}

/**
 * Check if search context exists
 */
export function hasSearchContext(): boolean {
  return getSearchContext() !== null;
}

